import axios from "axios";

export const createNote = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/notes`, data);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const getQuoteRequestNotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get(`/quotes/requests/${data?.id}/notes`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getQuoteNotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get(`/quotes/${data?.id}/notes`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getInvoiceNotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get(`/invoices/${data?.id}/notes`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getProjectNotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get(`/projects/${data?.id}/notes`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const getPONotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {	
	const params: any = {params : query, excludeInterceptor}
	if(controller?.signal){
		params.signal = controller.signal
	}

	try {
		const res: any = await axios.get(`/purchase-orders/${data?.id}/notes`);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

/**
 * Client Detail Notes tab (MASTER_PLAN.md §3.9 work item #12). Unlike
 * the sibling getXNotes helpers above, there is no dedicated
 * `/clients/:id/notes` sub-route on the backend — Client notes go
 * through the generic `GET /notes` endpoint with entityType/entityId
 * filters (see note.service.ts findAll, which already supports
 * arbitrary filters.entityType / filters.entityId via the shared
 * SQLfilter mechanism — no backend route needed for this call shape).
 */
export const getClientNotes = async ({ query = {}, controller = null, excludeInterceptor = false, data }: any) => {
	const params: any = {
		params: {
			...query,
			filters: {
				...(query?.filters ?? {}),
				entityType: "Client",
				entityId: data?.id,
			},
		},
		excludeInterceptor,
	};
	if (controller?.signal) {
		params.signal = controller.signal;
	}

	try {
		const res: any = await axios.get(`/notes`, params);
		return res?.data ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const updateNote = async ({ data, update }: any) => {
	try {
		const res: any = await axios.put(`/notes/${data?.id}`, update);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const deleteNote = async ({ data }: any) => {
	try {
		const res: any = await axios.delete(`/notes/${data?.id}`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/**
 * SESSION 45 GAP-68 — pin / unpin a note. Company staff only
 * (backend refuses with 403 for client / provider roles). Response
 * echoes back the updated note record with `pinned`, `pinnedAt`,
 * `pinnedBy` populated (or cleared, on unpin), so the caller can
 * replace the row in local state without re-fetching the full list.
 */
export const pinNote = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/notes/${data?.id}/pin`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

export const unpinNote = async ({ data }: any) => {
	try {
		const res: any = await axios.post(`/notes/${data?.id}/unpin`);
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/**
 * N1 Release 1 - Location notes (client portal).
 *
 * `getLocationNotes` hits the dedicated `GET /notes/location/:locationId`
 * route, which applies the visibility filter server-side by the viewer's
 * role/org (staff see all; a client sees clientVisible notes + their own
 * org's). Returns an array. `setNoteClientVisibility` is the staff-only
 * toggle that opts an internal note in/out of the client's view.
 *
 * Creating a location note reuses the generic `createNote` with
 * entityType "Location" + entityId = locationId (+ optional clientVisible).
 */
export const getLocationNotes = async ({ data, controller = null, excludeInterceptor = false }: any) => {
	const params: any = { excludeInterceptor };
	if (controller?.signal) params.signal = controller.signal;
	try {
		const res: any = await axios.get(`/notes/location/${data?.id}`, params);
		return Array.isArray(res?.data) ? res.data : [];
	} catch (e: any) {
		return [];
	}
};

export const setNoteClientVisibility = async ({ data, visible }: any) => {
	try {
		const res: any = await axios.post(`/notes/${data?.id}/client-visibility`, { visible: !!visible });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/** Share a note with one provider, or stop sharing (providerId null). Staff only. */
export const shareNote = async ({ id, providerId }: { id: string; providerId: string | null }) => {
	try {
		const res: any = await axios.put(`/notes/${id}/share`, { providerId });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};

/** Show / hide a note from the client (e.g. a provider's note). Staff only. */
export const setNoteClientAccess = async ({ id, visible }: { id: string; visible: boolean }) => {
	try {
		const res: any = await axios.put(`/notes/${id}/client-access`, { visible });
		return res?.data ? res.data : {};
	} catch (e: any) {
		return e?.response?.data ? e.response.data : e;
	}
};
